import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Stethoscope,
  Activity,
  ClipboardList,
  Calendar,
  UserCircle,
  Building2,
  ShieldCheck,
  AlertTriangle,
  LineChart,
  Users,
  LogOut,
  Menu,
  X,
  BadgeCheck,
  Lock,
  MailCheck,
  Users2,
  Rocket,
  Sparkles,
  FlaskConical,
  ChevronDown,
  Megaphone,
  Pill,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";
import TrialBanner from "@/components/TrialBanner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AppHeaderMenu from "@/components/AppHeaderMenu";
import GlobalSearch from "@/components/GlobalSearch";
import { ProChip } from "@/components/UpgradeGate";

// Common groups reused across roles
function makeGroups(role) {
  const testid = (suffix) => `nav-${role}-${suffix}`;
  const trustIndex  = { id: "trust",        label: "Trust Index",  direct: true, to: role === "hospital" ? "/hospital/trust" : "/directory" };
  const croInsights = { id: "cro-insights", label: "CRO Insights", direct: true, to: "/cro" };
  const directory   = { id: "directory",    label: "Directory",    direct: true, to: "/directory" };
  const network     = { id: "network",      label: "Network",      direct: true, to: "/network" };
  const startups    = { id: "startups",     label: "Startups",     direct: true, to: "/startups" };
  const account = (billing = true) => ({
    id: "account", label: "Account", description: "Billing & privacy",
    items: [
      ...(billing ? [{ to: "/billing", label: "Billing & Plan", description: "Subscription & invoices", icon: Sparkles, testid: testid("billing") }] : []),
      { to: "/settings/privacy", label: "Privacy & Data", description: "DPDP consent controls", icon: Lock, testid: testid("privacy") },
    ],
  });
  const shared = [trustIndex, croInsights, directory, network, startups];

  switch (role) {
    case "doctor":
      return [
        { id: "practice", label: "My Practice", description: "Clinic operations", items: [
          { to: "/doctor", label: "Overview", description: "Practice dashboard", icon: LayoutDashboard, testid: testid("overview") },
          { to: "/doctor/appointments", label: "Appointments", description: "Schedule & patients", icon: Calendar, testid: testid("appointments") },
          { to: "/doctor/analytics", label: "Practice Analytics", description: "KPIs & trends", icon: LineChart, testid: testid("analytics") },
          { to: "/patient-reach", label: "Patient Reach", description: "Acquisition system", icon: Megaphone, testid: "doctor-nav-forevency" },
          { to: "/clinic-growth", label: "ClinicGrowth · CareOS", description: "Campaigns + AI triage", icon: Stethoscope, testid: "doctor-nav-clinic-growth" },
          { to: "/doctor/profile", label: "My Profile", description: "Public listing", icon: UserCircle, testid: testid("profile") },
        ]},
        ...shared,
        account(),
      ];
    case "hospital":
      return [
        { id: "growth", label: "Growth", description: "Patient acquisition", items: [
          { to: "/patient-reach", label: "Patient Reach", description: "Acquisition system", icon: Megaphone, testid: "hospital-nav-forevency" },
          { to: "/clinic-growth", label: "ClinicGrowth · CareOS", description: "Campaigns + AI triage", icon: Stethoscope, testid: "hospital-nav-clinic-growth" },
        ]},
        ...shared, account(),
      ];
    case "admin":
      return [
        { id: "platform", label: "Platform", description: "System control", items: [
          { to: "/admin", label: "System Overview", description: "Portfolio metrics", icon: LayoutDashboard, testid: testid("overview") },
          { to: "/admin/users", label: "Users", description: "Accounts & roles", icon: Users, testid: testid("users") },
          { to: "/admin/verification", label: "Verifications", description: "Trust badge queue", icon: BadgeCheck, testid: testid("verify") },
          { to: "/admin/grievances", label: "DPDP Grievances", description: "Regulator inbox", icon: MailCheck, testid: testid("grievances") },
          { to: "/admin/genome", label: "Genome Review", description: "Counsellor queue", icon: FlaskConical, testid: testid("genome") },
          { to: "/admin/cro-investigators", label: "CRO Investigators", description: "PI editor", icon: FlaskConical, testid: testid("cro-pis") },
          { to: "/admin/dispenses", label: "Dispense Ledger", description: "Pharmacy attestations", icon: Pill, testid: testid("dispenses") },
          { to: "/admin/errors", label: "Error Logs", description: "First-party diagnostics", icon: AlertTriangle, testid: testid("errors") },
        ]},
        ...shared,
        account(false),
      ];
    case "patient":
    default:
      return [
        { id: "health", label: "My Health", description: "Personal care", items: [
          { to: "/patient", label: "Overview", description: "Health snapshot", icon: LayoutDashboard, testid: testid("overview") },
          { to: "/patient/triage", label: "Symptom Triage", description: "AI symptom checker", icon: Activity, testid: testid("triage") },
          { to: "/patient/appointments", label: "Appointments", description: "Bookings & history", icon: Calendar, testid: testid("appointments") },
          { to: "/patient/records", label: "Medical Records", description: "Prescriptions & reports", icon: ClipboardList, testid: testid("records") },
        ]},
        ...shared,
        account(),
      ];
  }
}


const ROLE_META = {
  patient:  { label: "Patient Portal",     icon: Stethoscope },
  doctor:   { label: "Doctor Excellence",  icon: Stethoscope },
  hospital: { label: "Hospital Command",   icon: Building2 },
  admin:    { label: "Platform Admin",     icon: ShieldCheck },
};

export default function AppShell({ children, pageTitle, pageDescription, actions }) {
  const { user, logout } = useAuth();
  const { is_pro } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role || "patient";
  const groups = makeGroups(role);
  const meta = ROLE_META[role] || ROLE_META.patient;
  const showTrialBanner = user && user !== false && user.role !== "admin";

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar (brand + user chip + language + CRO pill + logout) */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand">
            <BrandMark />
          </Link>
          <div className="hidden lg:flex items-center gap-2.5 rounded-md bg-muted/60 px-3 py-1.5">
            <meta.icon size={14} className="text-primary" />
            <div className="leading-tight">
              <div className="overline text-[9px]">{meta.label}</div>
              <div className="text-xs font-medium flex items-center gap-1.5" data-testid="user-name">
                {user?.name || user?.email}
                {is_pro && role !== "admin" && <ProChip testid="header-pro-chip" />}
              </div>
            </div>
          </div>
          <div className="flex-1" />
          <div className="hidden md:block">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/cro"
              data-testid="topnav-cro"
              className={cn(
                "hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition-all",
                location.pathname.startsWith("/cro")
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-accent/10 text-accent border-accent/40 hover:bg-accent/20"
              )}
            >
              <FlaskConical size={12} /> CRO Stack
            </Link>
            <LanguageSwitcher variant="compact" />
            <button
              onClick={onLogout}
              className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={13} /> Sign out
            </button>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-nav-toggle"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Desktop grouped menu */}
        <div className="hidden lg:block border-t border-border">
          <div className="max-w-[1600px] mx-auto px-4 lg:px-8 overflow-x-auto">
            <AppHeaderMenu groups={groups} activePath={location.pathname} />
          </div>
        </div>

        {/* Mobile CRO pill quick access + search */}
        <div className="lg:hidden px-4 pb-3 space-y-2">
          <Link
            to="/cro"
            data-testid="topnav-cro-mobile"
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors",
              location.pathname.startsWith("/cro")
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-accent/10 text-accent border-accent/40 hover:bg-accent/20"
            )}
          >
            <FlaskConical size={12} /> CRO Stack
          </Link>
          <div className="md:hidden">
            <GlobalSearch />
          </div>
        </div>
      </header>

      {/* Mobile slide-out menu */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMobileOpen(false)}>
          <div
            className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-nav-drawer"
          >
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <meta.icon size={16} className="text-primary" />
                <div className="text-sm font-medium truncate max-w-[180px]" data-testid="user-name-mobile">
                  {user?.name || user?.email}
                  {is_pro && role !== "admin" && <ProChip testid="mobile-pro-chip" />}
                </div>
              </div>
              <button className="p-1.5 rounded-md hover:bg-muted" onClick={() => setMobileOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-2">
              {groups.map((group) => (
                <MobileGroup key={group.id} group={group} activePath={location.pathname} onNavigate={() => setMobileOpen(false)} />
              ))}
            </div>
            <div className="p-3 border-t border-border">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                data-testid="logout-button-mobile"
              >
                <LogOut size={16} />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 min-h-0">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6">
          <div className="mb-6">
            <div className="overline">Arogya Pramana</div>
            <h1 className="mt-1 text-2xl lg:text-3xl font-serif font-medium text-primary" data-testid="page-title">
              {pageTitle}
            </h1>
            {pageDescription && (
              <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{pageDescription}</p>
            )}
            {actions && <div className="mt-3">{actions}</div>}
          </div>

          {showTrialBanner && (
            <div className="mb-6"><TrialBanner /></div>
          )}
          {children}
        </div>
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
          <Footer compact />
        </div>
      </main>
    </div>
  );
}

function MobileGroup({ group, activePath, onNavigate }) {
  // Direct link — render as a single row, no accordion
  if (group.direct && group.to) {
    const isSelected = activePath === group.to || activePath.startsWith(group.to);
    return (
      <div className="mb-1">
        <Link
          to={group.to}
          data-testid={`mobile-group-${group.id}`}
          onClick={onNavigate}
          className={cn(
            "w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors",
            isSelected ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
          )}
        >
          {group.label}
        </Link>
      </div>
    );
  }
  return <MobileAccordionGroup group={group} activePath={activePath} onNavigate={onNavigate} />;
}

function MobileAccordionGroup({ group, activePath, onNavigate }) {
  const hasActive = group.items.some((it) => activePath === it.to || (it.to !== "/" && activePath.startsWith(it.to)));
  const [open, setOpen] = useState(hasActive);
  return (
    <div className="mb-1">
      <button
        type="button"
        data-testid={`mobile-group-${group.id}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted"
      >
        {group.label}
        <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <ul className="mt-1 pl-2 space-y-0.5">
          {group.items.map((it) => {
            const isSelected = activePath === it.to || (it.to !== "/" && activePath.startsWith(it.to));
            const Icon = it.icon;
            return (
              <li key={it.to}>
                <Link
                  to={it.to}
                  data-testid={it.testid}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isSelected ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted hover:text-primary"
                  )}
                >
                  {Icon && <Icon size={15} />}
                  <span>{it.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function BrandMark({ size = 54 }) {
  return (
    <img
      src="/logo-arogya.png"
      alt="Arogya Pramana — Trust Infrastructure"
      height={size}
      style={{ height: size, width: "auto", display: "block" }}
      data-testid="brand-mark"
    />
  );
}
